import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { DeliveryOption } from "../../types";
import { useUpdateDeliveryOption } from "../../../../views/Orders/hooks/useUpdateDeliveryOption";
import { OrderPayableBy, OrderPayableTo } from "../../../../views/Orders/enums";
import { isInvalidPaymentRouteCombination } from "../utils/paymentRouteValidation";

export interface PaymentRoutePillProps {
  estimateNo: string;
  splitId: string | undefined;
  option: DeliveryOption;
  onOptionUpdate: (updatedOption: DeliveryOption) => void;
  isExpired: boolean;
}

export const PaymentRoutePill: React.FC<PaymentRoutePillProps> = ({
  estimateNo,
  splitId,
  option,
  onOptionUpdate,
  isExpired,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [payableBy, setPayableBy] = useState<OrderPayableBy | "">(
    option.payableBy || "",
  );
  const [payableTo, setPayableTo] = useState<OrderPayableTo | "">(
    option.payableTo || "",
  );

  useEffect(() => {
    if (isOpen) {
      setPayableBy(option.payableBy || "");
      setPayableTo(option.payableTo || "");
    }
  }, [isOpen, option.payableBy, option.payableTo]);

  const { mutate: updateDeliveryOption, isLoading: isUpdating } =
    useUpdateDeliveryOption();

  const isPaymentRouteInvalid = isInvalidPaymentRouteCombination(
    payableBy,
    payableTo,
  );

  const handleSave = () => {
    if (isPaymentRouteInvalid) return;
    if (!splitId || !payableBy || !payableTo) {
      toast({
        title: "Selection missing",
        description: "Please select both Payable By and Payable To",
        status: "warning",
        duration: 2000,
        position: "top",
      });
      return;
    }
    updateDeliveryOption(
      {
        estimateNo,
        splitId: splitId!,
        optionId: option._id,
        payload: { payableBy, payableTo },
      },
      {
        onSuccess: (resp: any) => {
          toast({
            title: "Payment Route updated",
            status: "success",
            duration: 2000,
            position: "top",
          });

          if (resp?.deliveryOption) {
            onOptionUpdate(resp.deliveryOption);
          } else {
            onOptionUpdate({
              ...option,
              payableBy,
              payableTo,
            } as DeliveryOption);
          }

          onClose();
        },
        onError: (err) => {
          toast({
            title: "Failed to update Payment Route",
            description: String(err),
            status: "error",
            duration: 3000,
            position: "top",
          });
        },
      },
    );
  };

  if (!splitId || !option.payableBy || !option.payableTo) return null;

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="right"
      trigger="hover"
      openDelay={100}
      closeOnBlur={true}
      isLazy
    >
      <PopoverTrigger>
        <Badge
          cursor={isExpired ? "not-allowed" : "pointer"}
          bg="transparent"
          color="purple.600"
          border="1px"
          borderColor="purple.300"
          fontSize="0.65rem"
          fontWeight="800"
          px={2}
          py={0.5}
          borderRadius="md"
          textTransform="uppercase"
          fontFamily="'Nunito','Inter', sans-serif"
        >
          ₹ {option.payableBy || "-"} → {option.payableTo || "-"}
          <Box
            as="span"
            display="inline-block"
            transform="rotate(-290deg)"
            ml={1}
          >
            ✎
          </Box>
        </Badge>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          zIndex={1600}
          width="180px"
          boxShadow="lg"
          onClick={(e) => e.stopPropagation()}
        >
          <PopoverBody>
            <VStack spacing={2} align="stretch" mt={1}>
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="#4A5568"
                  mb={1}
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Payable By
                </Text>
                <Select
                  size="xs"
                  value={payableBy}
                  onChange={(e) =>
                    setPayableBy(e.target.value as OrderPayableBy)
                  }
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                  color="#2D3748"
                >
                  {Object.values(OrderPayableBy).map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="#4A5568"
                  mb={1}
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Payable To
                </Text>
                <Select
                  size="xs"
                  value={payableTo}
                  onChange={(e) =>
                    setPayableTo(e.target.value as OrderPayableTo)
                  }
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                  color="#2D3748"
                >
                  {Object.values(OrderPayableTo).map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </Select>
              </Box>
              {isPaymentRouteInvalid && (
                <Text
                  fontSize="xs"
                  color="red.500"
                  fontWeight="600"
                  fontFamily="'Nunito','Inter', sans-serif"
                  lineHeight="short"
                >
                  The selected case is invalid. Please choose a valid case.
                </Text>
              )}
              <HStack spacing={2} pt={2}>
                <Button
                  size="xs"
                  variant="ghost"
                  flex="1"
                  onClick={() => {
                    setPayableBy(option.payableBy!);
                    setPayableTo(option.payableTo!);
                    onClose();
                  }}
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                  color="#2D3748"
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  colorScheme="blue"
                  flex="1"
                  onClick={handleSave}
                  isLoading={isUpdating}
                  isDisabled={isExpired || isPaymentRouteInvalid}
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                >
                  Save
                </Button>
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
